import React from 'react';
import { X, User, Activity, FileText } from 'lucide-react';



const AuditLogDetails = ({ log, onClose }) => {
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-50 border-red-200';
      case 'ERROR': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'WARNING': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getChanges = () => {
    if (!log?.old_values || !log?.new_values) return [];
    
    const oldVals = log?.old_values;
    const newVals = log?.new_values;
    const changes = [];

    Object.keys(newVals)?.forEach(key => {
      if (JSON.stringify(oldVals?.[key]) !== JSON.stringify(newVals?.[key])) {
        changes?.push({
          field: key,
          oldValue: oldVals?.[key],
          newValue: newVals?.[key]
        });
      }
    });

    return changes;
  };

  const changes = getChanges();

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Detalhes do Log de Auditoria</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Data/Hora</p>
            <p className="mt-1 text-sm text-gray-900">
              {formatDateTime(log?.event_timestamp)}
            </p>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">Usuário</p>
              <p className="text-sm font-medium text-gray-900">{log?.user_email}</p>
              <p className="text-xs text-gray-500 capitalize">{log?.user_role}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <Activity className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-600">Ação</p>
              <p className="text-sm font-medium text-gray-900">{log?.action_type}</p>
              <p className="text-xs text-gray-500">{log?.entity_type}</p>
            </div>
          </div>

          <div className="col-span-2 flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <FileText className="w-5 h-5 text-gray-600" />
            <div className="flex-1">
              <p className="text-xs text-gray-600">Entidade</p>
              <p className="text-sm font-medium text-gray-900">{log?.entity_name || 'N/A'}</p>
              <p className="text-xs text-gray-500 font-mono">{log?.entity_id}</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(log?.severity_level)}`}>
              {log?.severity_level}
            </div>
          </div>
        </div>

        {/* Changes Summary */}
        {log?.changes_summary && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumo das Mudanças</h4>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">{log?.changes_summary}</p>
            </div>
          </div>
        )}

        {/* Reason */}
        {log?.reason && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Motivo</h4>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-gray-700">{log?.reason}</p>
            </div>
          </div>
        )}

        {/* Field-Level Changes */}
        {changes?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Alterações Campo a Campo</h4>
            <div className="space-y-3">
              {changes?.map((change, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-700">{change?.field}</p>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-gray-200">
                    <div className="p-3 bg-red-50">
                      <p className="text-xs text-red-600 font-medium mb-1">Valor Anterior</p>
                      <p className="text-sm text-gray-900 font-mono break-words">
                        {JSON.stringify(change?.oldValue, null, 2)}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50">
                      <p className="text-xs text-green-600 font-medium mb-1">Novo Valor</p>
                      <p className="text-sm text-gray-900 font-mono break-words">
                        {JSON.stringify(change?.newValue, null, 2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session Information */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Informações da Sessão</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Endereço IP</p>
              <p className="text-sm font-mono text-gray-900">{log?.ip_address || 'N/A'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">ID da Sessão</p>
              <p className="text-sm font-mono text-gray-900 truncate">{log?.session_id || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* User Agent */}
        {log?.user_agent && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">User Agent</h4>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-mono text-gray-700 break-words">{log?.user_agent}</p>
            </div>
          </div>
        )}

        {/* Metadata */}
        {log?.metadata && Object.keys(log?.metadata)?.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Metadados Adicionais</h4>
            <div className="p-3 bg-gray-50 rounded-lg">
              <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                {JSON.stringify(log?.metadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

export default AuditLogDetails;